import * as Fetch from "../utils/Fetch";
import moment from "moment";
import { IModuleEntity, IRolePerrmission, IAccessRelationData } from "../controls/RelationAccesses/AccessDomainModels";
import { IResourceForAuthorizationEntity, IAuthorizationRelationData } from "../controls/RelationAuthorizations/AuthorizationDomainModel";
import { IRelationData, IBaseSearchResult } from "../controls/Relations/Models";
import { IErrorMessageJsonData } from "../controls/Relations/RequestControl";
import { IResourceForPersonProfileEntity } from "../../workflow/controls/PersonProfileRelations/PersonProfileDomainModel";

export enum ALERT {
	Loading = 1,
	NoData = 2,
	NoAccess = 3,
	Access = 4,
	/* Bład przy ściąganiu danych.*/
	Error = 5,
}

export interface IResourceJsonDataReply {
	id: number,
	name: string,
	type: string,
	kind: string,
	label: string
}

export interface IAccessInitDataReply {
	id: string;
	relationId?: number;
	employee: IResourceJsonDataReply;
	resource: IModuleEntity;

	role: { id: number, name: string };
	login: string;
	password: string;
	passwordExist: boolean | null,
	systemName: string;
	environment: string;
	referenceNumber: string;
	personAccountDescription: string,
	personAccountType: string,
	company: string,
	telephone: string,
	email: string,
	location: string,

	description: string;
	from: string;
	beforeTo: string;
	to: string;
	excluded: string[];
	deleted: boolean;
	errorMessages: IErrorMessageJsonData[],
}

export interface IAccessesInitDataReply {
	success: boolean,
	logSystemEnabled: boolean,
	value: IAccessInitDataReply[],
	rolePermissionsForValues: IRolePerrmission[]
	currentUser: IResourceJsonDataReply,
}

export interface IIdNamePair {
	id: number,
	name: string
}

export interface IAuthorizationsInitDataReply {
	success: boolean,
	logSystemEnabled: boolean,
	value: IAuthorizationInitDataReply[],
	currentUser: IResourceJsonDataReply,
	authorizationLevels: IIdNamePair[]
}

export interface IAuthorizationInitDataReply {
	id: string;
	relationId?: number;
	employee: IResourceJsonDataReply;
	resource: IResourceForAuthorizationEntity;
	level: IIdNamePair;
	from: string;
	to: string;
	deleted: boolean;
	errorMessages: IErrorMessageJsonData[];
}

export interface IPersonProfilesInitDataReply {
	success: boolean,
	logSystemEnabled: boolean,
	value: IPersonProfileInitDataReply[],
	currentUser: IResourceJsonDataReply,
}

export interface IPersonProfileInitDataReply {
	id: string;
	relationId?: number;
	employee: IResourceJsonDataReply;

	resource: IResourceForPersonProfileEntity;
	
	from: string;
	to: string;
	beforeTo: string;

	deleted: boolean;
	errorMessages: IErrorMessageJsonData[];
}

export enum SearchResourceKind {
	BusinessProcess,
	DataSet,
	Training,
	Employee,
	Application,
	ServiceAsset,
	ResourceLocation,
	PersonProfile,
}

export type SearchResourceKindString =
	| ""
	| keyof typeof SearchResourceKind;

export interface IAutocompleteResult {
	options: IOption[],
	searchText: string,
}

export interface IOption {
	id: number,
	value: string,
	label: string,
}

export interface ISearchAccessesResult extends IBaseSearchResult
{
	id: number,
	kind: string,
	resourceId: number,
	employeeId: number,
	rolePermission: string,
	isActive: boolean,
	resourceStatus: string,
	relationStatus: string,
	skipAccessExpirationDate: boolean,
	login: string,
	environment: string,
}

interface ISearchAuthorizationResult extends IBaseSearchResult {
	id: number,
	kind: string,
	resourceId: number,
	employeeId: number,
	level: string,
	isActive: boolean,
	resourceStatus: string,
	relationStatus: string,
}

export interface IEmployeesAutocompleteParams {
	forAcceptant: boolean,
	subordinatesTree: boolean,
	maxRows: number,
	term: string,
	filterId: string,
	filters: string,
}

export interface IAutocompleteParams {
	maxRows: number,
	filterId?: string,
}

interface GetResourcesAutocompleteResult {
	AcceptantId: number | null,
	ApplicationName: string,
	BarCode: string,
	Category: string,
	Company: string,
	Department: string,
	EmployeeCompany: string,
	GivenName: string,
	Surname: string,
	EmployeeId: number,
	EmployeeName: string,
	HRId: string,
	Id: number,
	IssueId: number,
	Kind: string, // Tłumaczona nazwa
	KindEnum: number,
	Manufacturer: string,
	Model: string,
	MpkNumber: string,
	Name: string,
	ReferenceNumber: string,
	Relation: string,
	RelationId: number,
	SAPId: string,
	STNumber: string,
	Selected: string,
	SerialNumber: string,
	Status: string,
	Type: string,
}

export type AccessesFromLog = { [key: string]: (ISearchAccessesResult[] | "error" | "") };

export class ResourceRelationsService {

	private accessesFromLog: AccessesFromLog = {};
	private authorizationsFromLog: { [key: string]: (ISearchAuthorizationResult[] | "error" | "") } = {};

	checkAuthorizesInLog(rows: IAuthorizationRelationData[], callback) {
		let filters = {
			filterResourcesIds: [],
			filterEmployeesIds: [],
		}
		let loadKeys = this.loadKeysorUpdateAuthorization(rows, filters)

		if (filters.filterEmployeesIds.length !== 0) {
			this.authorizationSearch(filters, (data) => {
				this.loadCheckAuthorizationInLog(data.authorizations, loadKeys, callback);
			});
		}
	}

	loadKeysorUpdateAuthorization(rows: IAuthorizationRelationData[], filters) {
		let loadKeys = [];
		for (let i = 0; i < rows.length; i++) {
			let row = rows[i];
			if (!row.employee?.id) {
				row.alert = ALERT.NoAccess;
				continue;
			}

			if (!row.alert || row.alert === ALERT.Loading) {
				row.alert = ALERT.Loading;

				let searchkey = row.employee.id + ":" + row.resource.id;
				let cacheData = this.authorizationsFromLog;

				if (Array.isArray(cacheData[searchkey])) {

					row.alert = this.getAuthorizationAlertCode(row);
				}
				else if (cacheData[searchkey] === undefined) {
					filters.filterEmployeesIds.push(row.employee.id);
					filters.filterResourcesIds.push(row.resource.id);
					cacheData[searchkey] = "";
					loadKeys.push(searchkey);
				}
			}
		}

		return loadKeys;
	}

	loadCheckAuthorizationInLog(authorizations: ISearchAuthorizationResult[], loadedKeys, callback) {

		for (let i = 0; i < loadedKeys.length; i++) {
			this.authorizationsFromLog[loadedKeys[i]] = [];
		}
		if (Array.isArray(authorizations)) {
			for (let i = 0; i < authorizations.length; i++) {
				let authorization = authorizations[i];

				let accesseskey = authorization.employeeId + ":" + authorization.resourceId;
				if (!Array.isArray(this.authorizationsFromLog[accesseskey])) {
					this.authorizationsFromLog[accesseskey] = [];
				}
				(this.authorizationsFromLog[accesseskey] as ISearchAuthorizationResult[]).push(authorization);
			}
		}
		callback();
	}

	getAuthorizationAlertCode(row: IAuthorizationRelationData): ALERT {

		if (row.alert && row.alert != ALERT.Loading)
			return row.alert;

		if (!row.employee?.id)
			return ALERT.NoAccess;

		let authorizationkey = row.employee.id + ":" + row.resource.id;
		let authorizations = this.authorizationsFromLog[authorizationkey];
		if (authorizations === "error")
			return ALERT.Error;
		if (!Array.isArray(authorizations))
			return ALERT.Loading;

		for (let i = 0; i < authorizations.length; i++) {
			let authorization = authorizations[i];

			if (!authorization.isActive) {
				continue;
			}

			if (!row.level && !authorization.level) {
				return ALERT.Access;
			}

			if (this.hasTime(row, authorization) && row.level && authorization.level == row.level.name) {
				return ALERT.Access;
			}
		}

		return ALERT.NoAccess;
	}

	loadAccessesFromLog(rows: IAccessRelationData[], callback: (data: AccessesFromLog) => void) {
		const filters = {
			filterResourcesIds: [],
			filterEmployeesIds: []
		};

		const loadKeys = this.loadKeysOrUpdateAccess(rows, filters)
		
		if (filters.filterEmployeesIds.length !== 0) {
			this.accessesSearch(filters, (data) => {
				if (data.success)
					this.loadCheckAccessesInLog(data.accesses, loadKeys);
				else {
					this.loadFailedAccessesInLog(loadKeys);
				}

				callback(this.accessesFromLog);
			});
		}
	}

	private loadKeysOrUpdateAccess(rows: IAccessRelationData[], filters): string[] {
		const loadKeys: string[] = [];
		for (let i = 0; i < rows.length; i++) {
			const row = rows[i];
			if (!row.employee?.id) {
				continue;
			}

			const searchkey = row.employee.id + ":" + row.resource.id;

			if (this.accessesFromLog[searchkey] === undefined) {
				filters.filterEmployeesIds.push(row.employee.id);
				filters.filterResourcesIds.push(row.resource.id);
				this.accessesFromLog[searchkey] = "";
				loadKeys.push(searchkey);
			}
		}

		return loadKeys;
	}
	
	private loadCheckAccessesInLog(accesses: ISearchAccessesResult[], loadedKeys: string[]) {

		for (let i = 0; i < loadedKeys.length; i++) {
			this.accessesFromLog[loadedKeys[i]] = [];
		}
		if (Array.isArray(accesses)) {
			for (let i = 0; i < accesses.length; i++) {
				let access = accesses[i];

				let accesseskey = access.employeeId + ":" + access.resourceId;
				if (!Array.isArray(this.accessesFromLog[accesseskey])) {
					this.accessesFromLog[accesseskey] = [];
				}

				(this.accessesFromLog[accesseskey] as ISearchAccessesResult[]).push(access);
			}
		}
	}

	private loadFailedAccessesInLog(loadedKeys: string[]) {

		for (let i = 0; i < loadedKeys.length; i++) {
			this.accessesFromLog[loadedKeys[i]] = "error";
		}
	}


	hasTime(row: IRelationData, access: IBaseSearchResult): boolean {

		if (access.from && row.to && moment(access.from.dateTimeO) >= row.from && moment(access.from.dateTimeO) >= row.to) {
			return false;
		}
		if (access.to && moment(access.to.dateTimeO) <= row.from && (!row.to || moment(access.to.dateTimeO) <= row.to)) {
			return false;
		}

		return true;
	}

	getAccessesInitData(issueId: number, callback: (data: IAccessesInitDataReply) => void) {
		Fetch.get<any>("/ResourceRelations/AccessesInitData/", {
			issueId: issueId,
		})
		.then(data => {
			if (data.success) {
				callback(data);
			}
		});
	}

	getAuthorizationsInitData(issueId: number, callback: (data: IAuthorizationsInitDataReply) => void): void {
		Fetch.get<any>("/ResourceRelations/AuthorizationInitData/", {
			issueId: issueId,
		})
		.then(data => {
			if (data.success) {
				callback(data);
			}
		});
	}

	getPersonProfilesInitData(issueId: number, callback: (data: IPersonProfilesInitDataReply) => void) {
		Fetch.get<any>("/ResourceRelations/PersonProfilesInitData/", {
			issueId: issueId,
		})
			.then(data => {
				if (data.success) {
					callback(data);
				}
			});
	}

	getServicesInitData(issueId: number, callback: (data) => void) {
		Fetch.get<any>("/ResourceRelations/ServicesInitData/", {
			issueId: issueId,
		})
			.then(data => {
				if (data.success) {
					callback(data);
				}
			});
	}

	getRolePermissions(applicationId: number, search: string, take: number, callback: (data: { rolePermissions: IRolePerrmission[] }) => void) {
		Fetch.get<any>("/ResourceRelations/GetRolePermissions/" + applicationId, {
			search: search,
			take: take,
		})
			.then(data => {
				if (data.success) {
					callback(data);
				}
			});
	}

	getResources(kind: SearchResourceKindString, params: IAutocompleteParams, term: string, callback: (result: IAutocompleteResult) => void) {
		
		const paramsToSend = {
			maxRows: params?.maxRows || 12,
			filterId: params?.filterId,
			startsWith: term,
			kind: kind
		}
		Fetch.get<any>("/Resources/GetResourcesAutocomplete/", paramsToSend)
			.then((data: GetResourcesAutocompleteResult[]) => {
				const result = data.map((x) => {
					let label: string = (window as any).qdesk.ui.util.resourceLabelText(x);
					var item: IOption = {
						id: x.Id,
						value: label,
						label: label,
					}
					return item;
				}).sort(this.compare);

				callback({
					options: result,
					searchText: term,
				});
			});
	}

	getEmployeesAutocomplete(params: IEmployeesAutocompleteParams, callback: (result: IAutocompleteResult) => void) {
		let url = "/Resources/GetEmployeesAutocomplete/";

		let paramsTosend = {
			maxRows: params.maxRows || 12,
			startsWith: params.term,
			subordinates: params.forAcceptant ? "True" : "False",
			subordinatesTree: params.subordinatesTree ? "True" : "False",
			filterId: params.filterId,
			filters: params.filters,
		};
		Fetch.get<any[]>(url, paramsTosend)
			.then(data => {
				const result = data.map((x) => {
					let label = (window as any).qdesk.ui.util.resourceLabelText(x);
					var item: IOption = {
						id: x.Id,
						value: label,
						label: label,
					}
					return item;
				}).sort(this.compare);

				callback({
					options: result,
					searchText: params.term,
				});
			});
	}

	getServiceResourcesAutocomplete(filters: IAutocompleteParams, term: string, callback: (result: IAutocompleteResult) => void) {
		let url = "/Resources/GetServiceResourcesAutocomplete/";

		let params = {
			maxRows: filters.maxRows || 12,
			startsWith: term,
			filterId: filters.filterId,
		};
		Fetch.get<any[]>(url, params)
			.then(data => {
				var result: IOption[] = data.map((x) => {
					let label = (window as any).qdesk.ui.util.resourceLabelText(x);
					var item: IOption = {
						id: x.Id,
						value: label,
						label: label,
					}
					return item;
				}).sort(this.compare);

				callback({
					options: result,
					searchText: term,
				});
			});
	}

	compare(a: IOption, b: IOption): number {
		if (a.label < b.label)
		return -1
		if (a.label > b.label)
		return 1
		return 0
	}

	accessesSearch(conf, callback: (data: any) => void) {

		let data = {
			filterApplicationsIds: "",
			filterEmployeesIds: "",
			filterRolePermissions: "",
			filterFrom: "",
			filterTo: "",
			skip: 0,
			take: 0
		};
		
		let formData = Object.assign({}, data, {
			filterEmployeesIds: conf.filterEmployeesIds,
			filterApplicationsIds: conf.filterResourcesIds,
		});

		formData.filterApplicationsIds = this.joinStrings(formData.filterApplicationsIds);
		formData.filterEmployeesIds = this.joinStrings(formData.filterEmployeesIds);
		formData.filterRolePermissions = this.joinStrings(formData.filterRolePermissions);

		Fetch.get<any>("/ResourceRelations/SearchAccesses", formData)
			.then(data => {
				callback(data);
			})
			.catch(error => {
				console.error(error);
				callback({});
			});
	}

	authorizationSearch(conf, callback) {

		let data = {
			filterResourcesIds: "",
			filterEmployeesIds: "",
			filterLevels: "",
			filterFrom: "",
			filterTo: "",
			skip: 0,
			take: 0
		};

		let formData = Object.assign({}, data, {
			filterEmployeesIds: conf.filterEmployeesIds,
			filterResourcesIds: conf.filterResourcesIds,
		});

		formData.filterResourcesIds = this.joinStrings(formData.filterResourcesIds);
		formData.filterEmployeesIds = this.joinStrings(formData.filterEmployeesIds);
		formData.filterLevels = this.joinStrings(formData.filterLevels);
		
		Fetch.get<any>("/ResourceRelations/SearchAuthorizations", formData)
			.then(data => {
				if (data.success) {
					callback(data);
				}
			});
	}
	
	joinStrings(array) {
		let result = "";
		for (let i = 0; i < array.length; i++) {
			if (result === "") {
				result = array[i];
			} else
			{
				result += ";" + array[i];
			}
		};
		return result;
	}
}